import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import PortalTable, { PortalTableColumn } from '../util/PortalTable';

const TABLE = 'crm_organisation';

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text', long_text: 'Long Text', number: 'Number', date: 'Date',
  date_range: 'Date Range', address: 'Address', image: 'Image', email: 'Email',
  phone: 'Phone', url: 'URL', select: 'Select', multi_select: 'Multi Select',
  checkbox: 'Checkbox', currency: 'Currency', file: 'File', user: 'User',
};

const MODAL_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 3,
};

function ValuesCell({ field }: { field: any }) {
  const { type, options } = field;
  if (type === 'checkbox') return <span className="opacity-60 text-[12px]">Yes / No</span>;
  if ((type === 'select' || type === 'multi_select') && Array.isArray(options) && options.length > 0) {
    return (
      <select className="edit-select py-[2px] px-[6px] rounded-[4px] edit-input text-[12px] max-w-[160px]"
        defaultValue="">
        <option value="" disabled>({options.length} options)</option>
        {options.map((o: string, i: number) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
  }
  return <span className="opacity-50 text-[12px]">{FIELD_TYPE_LABELS[type] ?? type}</span>;
}

function AddFieldModal({
  open, onClose, onSaved, editing, orgTypes,
}: {
  open: boolean; onClose: () => void; onSaved: () => void; editing?: any; orgTypes: any[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [type, setType] = useState('text');
  const [typeId, setTypeId] = useState('');
  const [options, setOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isShown, setIsShown] = useState(true);

  useEffect(() => {
    if (open && editing) {
      setName(editing.column_name ?? '');
      setType(editing.type ?? 'text');
      setTypeId(editing.typeId ? String(editing.typeId) : '');
      setOptions(Array.isArray(editing.options) ? editing.options.join(', ') : '');
      setIsRequired(editing.isRequired ?? false);
      setIsShown(editing.isShown ?? true);
    } else if (open) {
      setName(''); setType('text'); setTypeId(''); setOptions('');
      setIsRequired(false); setIsShown(true);
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar(t('Required'), { variant: 'error' }); return; }
    const optionsArr = ['select', 'multi_select'].includes(type)
      ? options.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined;
    const payload = {
      table_name: TABLE, column_name: name.trim(), type,
      typeId: typeId ? Number(typeId) : undefined,
      options: optionsArr, isRequired, isShown,
    };
    try {
      if (editing?.id) {
        await axios.put(`fields/${editing.id}`, payload);
        enqueueSnackbar(t('Change'), { variant: 'success' });
      } else {
        await axios.post('fields/add', payload);
        enqueueSnackbar(t('Add'), { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={MODAL_STYLE}>
          <div className="text-[15px] font-semibold mb-[12px] text-[var(--text-color)]">
            {editing ? t('Edit') : t('AddField')}
          </div>
          <div className="flex flex-col mb-[8px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">{t('Name')}</label>
            <input title={t('Name')} className="w-full text-[13px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex flex-col mb-[8px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">{t('FieldType')}</label>
            <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
              value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col mb-[8px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">Scope ({t('OrganisationType')})</label>
            <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
              value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">— All types —</option>
              {orgTypes.map((ot) => (
                <option key={ot.id} value={ot.id}>{ot.name}</option>
              ))}
            </select>
          </div>
          {['select', 'multi_select'].includes(type) && (
            <div className="flex flex-col mb-[8px]">
              <label className="text-[10px] ml-[5px] text-[var(--text-color)]">Options (comma-separated)</label>
              <input title="Options" className="w-full text-[13px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                value={options} onChange={(e) => setOptions(e.target.value)} />
            </div>
          )}
          <div className="flex items-center gap-[16px] mb-[12px] text-[13px] text-[var(--text-color)]">
            <label className={`flex items-center gap-[6px] ${isShown ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
              <input type="checkbox" title={t('Required')} disabled={!isShown} checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)} />
              {t('Required')}
            </label>
            <label className="flex items-center gap-[6px] cursor-pointer">
              <input type="checkbox" title={t('Visible')} checked={isShown} onChange={(e) => {
                const v = e.target.checked;
                setIsShown(v);
                if (!v) setIsRequired(false);
              }} />
              {t('Visible')}
            </label>
          </div>
          <div className="flex gap-[8px]">
            <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }} onClick={handleSave}>
              {editing ? t('Change') : t('Add')}
            </Button>
            <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }} onClick={onClose}>
              {t('Cancel')}
            </Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

export default function CrmOrganisationFields() {
  const { enqueueSnackbar } = useSnackbar();
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [orgTypes, setOrgTypes] = useState<any[]>([]);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const [{ data: fields }, { data: types }] = await Promise.all([
        axios.get(`fields/custom?table=${TABLE}`),
        axios.get('crm/organisation-types'),
      ]);
      setCustomFields(Array.isArray(fields) ? fields : []);
      setOrgTypes(Array.isArray(types) ? types : []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCustomToggle = async (field: any, prop: 'isShown' | 'isRequired', current: boolean) => {
    const updates: Record<string, boolean> = { [prop]: !current };
    if (prop === 'isShown' && current) updates.isRequired = false;
    try { await axios.put(`fields/${field.id}`, updates); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleDeleteField = async (id: number) => {
    try { await axios.delete(`fields/${id}`); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const columns: PortalTableColumn<any>[] = [
    {
      key: 'column_name', label: t('Name'),
      render: (f) => f.column_name,
    },
    {
      key: 'type', label: t('FieldType'),
      render: (f) => <span className="opacity-60">{FIELD_TYPE_LABELS[f.type] ?? f.type}</span>,
    },
    {
      key: 'values', label: t('Values'),
      render: (f) => <ValuesCell field={f} />,
    },
    {
      key: 'isShown', label: t('Visible'),
      render: (f) => (
        <Checkbox size="small" checked={f.isShown}
          onChange={() => handleCustomToggle(f, 'isShown', f.isShown)}
          sx={{ p: '2px' }} />
      ),
    },
    {
      key: 'isRequired', label: t('Required'),
      render: (f) => (
        <Checkbox size="small" checked={f.isRequired} disabled={!f.isShown}
          onChange={() => handleCustomToggle(f, 'isRequired', f.isRequired)}
          sx={{ p: '2px' }} />
      ),
    },
    {
      key: 'typeId', label: t('OrganisationType'),
      render: (f) => {
        const scopeLabel = f.typeId
          ? orgTypes.find((ot: any) => ot.id === f.typeId)?.name ?? '?'
          : 'All';
        return <span className="text-[11px] opacity-60">{scopeLabel}</span>;
      },
    },
  ];

  return (
    <>
      <PortalTable
        title={t('CustomFields')}
        rows={customFields}
        getKey={(f) => f.id}
        columns={columns}
        onAdd={() => { setEditingField(null); setAddFieldOpen(true); }}
        addLabel={`${t('Add')} ${t('CustomFields')}`}
        onEdit={(f) => { setEditingField(f); setAddFieldOpen(true); }}
        onDelete={(f) => handleDeleteField(f.id)}
        onDeleteMany={async (ids) => {
          await Promise.all(ids.map((id) => axios.delete(`fields/${id}`)));
          load();
        }}
      />
      <AddFieldModal
        open={addFieldOpen}
        onClose={() => setAddFieldOpen(false)}
        onSaved={load}
        editing={editingField}
        orgTypes={orgTypes}
      />
    </>
  );
}
